/**
 * Users Service
 * Handles user/customer management operations (Admin)
 */

import api from './api';

class UsersService {
  /**
   * Get all users with filters
   * @param {Object} filters - Filter options
   */
  async getUsers(filters = {}) {
    const params = new URLSearchParams();

    if (filters.page) params.append('page', filters.page);
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.role) params.append('role', filters.role);
    if (filters.search) params.append('search', filters.search);
    if (filters.isActive !== undefined) params.append('isActive', filters.isActive);
    if (filters.isSuspended !== undefined) params.append('isSuspended', filters.isSuspended);
    if (filters.isEmailVerified !== undefined) params.append('isEmailVerified', filters.isEmailVerified);
    if (filters.company) params.append('company', filters.company);
    if (filters.subscription) params.append('subscription', filters.subscription);

    const response = await api.get(`/admin/users?${params.toString()}`);

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error('Failed to fetch users');
  }

  /**
   * Get user statistics
   */
  async getUserStats() {
    const response = await api.get('/user/stats');

    if (response.success && response.data) {
      return response.data.stats || response.data;
    }

    throw new Error('Failed to fetch user stats');
  }

  /**
   * Get user by ID
   * @param {string} userId - User ID
   */
  async getUserById(userId) {
    const response = await api.get(`/admin/users/${userId}`);

    if (response.success && response.data) {
      return response.data.user || response.data;
    }

    throw new Error('Failed to fetch user');
  }

  /**
   * Create new user
   * @param {Object} userData - User data
   */
  async createUser(userData) {
    const response = await api.post('/admin/users', userData);

    if (response.success && response.data) {
      return response.data.user || response.data;
    }

    throw new Error('Failed to create user');
  }

  /**
   * Update user
   * @param {string} userId - User ID
   * @param {Object} updates - Updated data
   */
  async updateUser(userId, updates) {
    const response = await api.put(`/admin/users/${userId}`, updates);

    if (response.success && response.data) {
      return response.data.user || response.data;
    }

    throw new Error('Failed to update user');
  }

  /**
   * Delete user
   * @param {string} userId - User ID
   */
  async deleteUser(userId) {
    const response = await api.delete(`/admin/users/${userId}`);

    if (response.success) {
      return true;
    }

    throw new Error('Failed to delete user');
  }

  /**
   * Reset user password
   * @param {string} userId - User ID
   */
  async resetPassword(userId) {
    const response = await api.patch(`/admin/users/${userId}/reset-password`);

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error('Failed to reset password');
  }

  /**
   * Change user role
   * @param {string} userId - User ID
   * @param {string} roleId - Role ID
   */
  async changeRole(userId, roleId) {
    const response = await api.put(`/rbac/users/${userId}/role`, { roleId });

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error('Failed to change role');
  }

  /**
   * Get all available roles
   */
  async getRoles() {
    const response = await api.get('/rbac/roles');

    if (response.success && response.data) {
      return response.data.roles || response.data;
    }

    throw new Error('Failed to fetch roles');
  }

  /**
   * Toggle user active status
   * @param {string} userId - User ID
   * @param {boolean} isActive - Active status
   */
  async toggleStatus(userId, isActive) {
    return this.updateUser(userId, { isActive });
  }

  /**
   * Get customers only
   * @param {Object} filters - Filter options
   */
  async getCustomers(filters = {}) {
    return this.getUsers({
      ...filters,
      role: 'Customer',
    });
  }

  /**
   * Get admin users only
   * @param {Object} filters - Filter options
   */
  async getAdmins(filters = {}) {
    return this.getUsers({
      ...filters,
      role: 'Admin',
    });
  }

  /**
   * Suspend user
   * @param {string} userId - User ID
   * @param {string} reason - Suspension reason
   */
  async suspendUser(userId, reason = '') {
    const response = await api.post(`/user/${userId}/suspend`, { reason });

    if (response.success && response.data) {
      return response.data.user || response.data;
    }

    throw new Error('Failed to suspend user');
  }

  /**
   * Unsuspend user
   * @param {string} userId - User ID
   */
  async unsuspendUser(userId) {
    const response = await api.post(`/user/${userId}/unsuspend`);

    if (response.success && response.data) {
      return response.data.user || response.data;
    }

    throw new Error('Failed to unsuspend user');
  }

  /**
   * Get drivers only
   * @param {Object} filters - Filter options
   */
  async getDrivers(filters = {}) {
    return this.getUsers({
      ...filters,
      role: 'Driver',
    });
  }
}

const usersService = new UsersService();
export default usersService;
